import React from 'react';
import { UseFormRegister, UseFormWatch } from 'react-hook-form';
import { QUESTION_CARD, QUESTION_TITLE, QUESTION_ERROR } from '../card-classes';
import { countResponseWords } from 'utils/openEndedResponse';

interface QuestionTextareaProps {
  register: UseFormRegister<any>;
  watch: UseFormWatch<any>;
  errors: any;
  name: string;
  questionText: string;
  questionNumber: number;
  /** Words the answer must reach once the candidate starts writing. */
  minWords: number;
  placeholder: string;
  containerClassName?: string;
  titleClassName?: string;
  textareaContainerClassName?: string;
  textareaClassName?: string;
  characterCountClassName?: string;
  errorMessageClassName?: string;
}

export const QuestionTextarea: React.FC<QuestionTextareaProps> = ({
  register,
  watch,
  errors,
  name,
  questionText,
  questionNumber,
  minWords,
  placeholder,
  containerClassName,
  titleClassName,
  textareaContainerClassName,
  textareaClassName,
  characterCountClassName,
  errorMessageClassName,
}) => {
  const textValue = watch(name) || '';
  const registration = register(name);

  const wordCount = countResponseWords(textValue);
  // The word count is a floor, not a cap: an answer may be left empty, but a
  // started one has to reach the minimum. Only that middle state is short.
  const isBelowMinimum = wordCount > 0 && wordCount < minWords;

  // Empty states the rule, a short answer counts up to it, and a satisfied one
  // drops the target rather than reading as though it were a limit to stay under.
  const wordCountLabel = wordCount === 0
    ? `${minWords} words minimum`
    : isBelowMinimum
      ? `${wordCount}/${minWords} words minimum`
      : `${wordCount} words`;

  return (
    <div className={`${QUESTION_CARD} ${containerClassName || ''}`}>
      <h3 className={`${QUESTION_TITLE} ${titleClassName || ''}`}>
        {questionNumber}. {questionText}
      </h3>
      <div className={`relative ${textareaContainerClassName || ''}`}>
        <textarea
          {...registration}
          value={textValue}
          placeholder={placeholder}
          className={`w-full min-h-[120px] resize-y rounded-md border border-[#EAECF0] bg-[#FFFFFF66] p-3 font-[inherit] text-sm leading-[1.4] focus:border-[#3b82f6] focus:shadow-[0_0_0_3px_rgba(59,130,246,0.1)] focus:outline-none ${textareaClassName || ''}`}
          onPaste={(e) => e.preventDefault()}
        />
        <div
          className={`absolute bottom-2 right-3 text-xs text-[#6b7280] ${characterCountClassName || ''}`}
          style={isBelowMinimum ? { color: '#d92d20' } : undefined}
        >
          {wordCountLabel}
        </div>
      </div>
      {errors[name] && (
        <small className={`${QUESTION_ERROR} ${errorMessageClassName || ''}`}>
          {errors?.[name]?.message}
        </small>
      )}
    </div>
  );
};
